import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BarChart3, Plus } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import {
  fetchConversations,
  createConversation,
  fetchConversationWithMessages,
  downloadReportPdf,
  updateConversationTitle,
  archiveConversation,
} from '@/api/chat';
import { ReportBuilder } from './ReportBuilder';
import { ReportHistory } from './ReportHistory';
import { ReportPreview } from './ReportPreview';
import { useStreamingChat } from '@/hooks/useStreamingChat';
import { useToast } from '@/hooks/use-toast';
import type { Report } from '../types';

export function ReportsView({ initialReportId = null }: { initialReportId?: number | null }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState(initialReportId != null ? 'history' : 'create');
  const [activeReportId, setActiveReportId] = useState<number | null>(initialReportId);
  const [generatingId, setGeneratingId] = useState<number | null>(null);
  const [promptForActiveReport, setPromptForActiveReport] = useState<string | null>(null);
  const [rerunPrompt, setRerunPrompt] = useState<string | null>(null);
  /** Reset when content is cleared (e.g. after tool call) so we can update title from the new response */
  const lastTitleSentRef = useRef<string | null>(null);

  useEffect(() => {
    if (initialReportId != null) {
      setActiveReportId(initialReportId);
      setActiveTab('history');
    }
  }, [initialReportId]);

  const { isStreaming, streamedContent, streamedToolExecutions, streamMessage, reset } =
    useStreamingChat();

  const { data: conversations = [] } = useQuery({
    queryKey: ['conversations', 'report'],
    queryFn: () => fetchConversations('report'),
  });

  const reports: Report[] = conversations.map((conv) => ({
    id: conv.id,
    title: conv.title || 'Untitled Report',
    type: 'AI-Generated',
    status: generatingId === conv.id ? 'generating' : 'ready',
    createdAt: new Date(conv.updated_at),
    tokens: conv.total_tokens,
    responseTimeMs: conv.total_response_time_ms,
  }));

  const { data: activeConversation } = useQuery({
    queryKey: ['conversations', 'report', activeReportId],
    queryFn: () => fetchConversationWithMessages(activeReportId!),
    enabled: activeReportId != null,
  });

  const createReportMutation = useMutation({
    mutationFn: async (prompt: string) => {
      const firstLine = prompt.trim().split(/\n/)[0] ?? '';
      const title = firstLine.slice(0, 60).trim() || 'New report';
      return createConversation({ title, type: 'report' });
    },
  });

  useEffect(() => {
    if (generatingId == null || activeReportId !== generatingId) return;

    if (!streamedContent || streamedContent.trim() === '') {
      lastTitleSentRef.current = null;
      return;
    }

    const lines = streamedContent.split(/\r?\n/);
    if (lines.length < 2) return;

    const match = streamedContent.match(/^#+\s*(.+)$/m);
    const reportTitle = match?.[1]?.trim().slice(0, 255);
    if (!reportTitle || reportTitle === lastTitleSentRef.current) return;

    lastTitleSentRef.current = reportTitle;
    updateConversationTitle(generatingId, reportTitle)
      .then(({ title: newTitle }) => {
        queryClient.setQueryData(
          ['conversations', 'report', generatingId],
          (prev: Awaited<ReturnType<typeof fetchConversationWithMessages>> | undefined) =>
            prev ? { ...prev, title: newTitle } : prev
        );
        queryClient.setQueryData(
          ['conversations', 'report'],
          (prev: Awaited<ReturnType<typeof fetchConversations>> | undefined) =>
            prev?.map((c) => (c.id === generatingId ? { ...c, title: newTitle } : c)) ?? prev
        );
      })
      .catch(() => {
        lastTitleSentRef.current = null;
      });
  }, [streamedContent, generatingId, activeReportId, queryClient]);

  useEffect(() => {
    if (!isStreaming && generatingId != null) {
      lastTitleSentRef.current = null;
      queryClient.invalidateQueries({ queryKey: ['conversations', 'report'] });
      queryClient.invalidateQueries({
        queryKey: ['conversations', 'report', generatingId],
      });
      setGeneratingId(null);
    }
  }, [isStreaming, generatingId, queryClient]);

  const handleGenerate = useCallback(
    async (prompt: string) => {
      try {
        const conv = await createReportMutation.mutateAsync(prompt);
        setGeneratingId(conv.id);
        setActiveReportId(conv.id);
        setPromptForActiveReport(prompt);
        setActiveTab('history');
        reset();
        queryClient.invalidateQueries({ queryKey: ['conversations', 'report'] });
        await streamMessage(conv.id, prompt);
      } catch (error) {
        console.error('Failed to generate report:', error);
        setGeneratingId(null);
        setActiveReportId(null);
      }
    },
    [createReportMutation, streamMessage, reset, queryClient]
  );

  const handleOpenReport = useCallback((report: Report) => {
    setActiveReportId(report.id);
    setActiveTab('history');
  }, []);

  const handleRerun = useCallback(async (report: Report) => {
    try {
      const conv = await fetchConversationWithMessages(report.id);
      const firstUserMessage = conv.chat_message.find((m) => m.role === 'user');
      const promptText = firstUserMessage?.content?.trim() ?? '';
      if (promptText) {
        setRerunPrompt(promptText);
        setActiveTab('create');
      }
    } catch (e) {
      console.error('Failed to load report for rerun:', e);
    }
  }, []);

  const handleCloseReport = useCallback(() => {
    setActiveReportId(null);
    setGeneratingId(null);
    setPromptForActiveReport(null);
    reset();
  }, [reset]);

  const handleNewReport = useCallback(() => {
    setActiveReportId(null);
    setGeneratingId(null);
    setPromptForActiveReport(null);
    setRerunPrompt(null);
    reset();
    setActiveTab('create');
  }, [reset]);

  const handleDownload = useCallback(async (report: Report) => {
    try {
      const filename = `${report.title.replace(/[^a-zA-Z0-9]/g, '_')}_${report.id}.pdf`;
      await downloadReportPdf(report.id, filename);
    } catch (error) {
      console.error('Failed to download report:', error);
    }
  }, []);

  const archiveMutation = useMutation({
    mutationFn: (reportId: number) => archiveConversation(reportId),
    onSuccess: (_, reportId) => {
      if (activeReportId === reportId) {
        setActiveReportId(null);
        setGeneratingId(null);
        setPromptForActiveReport(null);
        reset();
      }
      queryClient.invalidateQueries({ queryKey: ['conversations', 'report'] });
      queryClient.removeQueries({ queryKey: ['conversations', 'report', reportId] });
      toast({ title: 'Report deleted', description: 'Removed from your report history.' });
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Could not delete report',
        description: 'Please try again.',
      });
    },
  });

  const handleDelete = useCallback(
    (report: Report) => {
      archiveMutation.mutate(report.id);
    },
    [archiveMutation]
  );

  const isGeneratingThisReport =
    activeReportId != null && generatingId === activeReportId && isStreaming;
  const userMessage = activeConversation?.chat_message.find((m) => m.role === 'user');
  const assistantMessage = activeConversation?.chat_message.find((m) => m.role === 'assistant');
  const displayContent = isGeneratingThisReport
    ? streamedContent
    : (assistantMessage?.content ?? '');
  const promptText =
    userMessage?.content ?? (activeReportId === generatingId ? promptForActiveReport : null) ?? '';
  const toolCount = isGeneratingThisReport
    ? streamedToolExecutions.length
    : (assistantMessage?.chat_function_result?.length ?? 0);
  const footerToolDetails = isGeneratingThisReport
    ? streamedToolExecutions.map((t) => ({
        name: t.name,
        executionTimeMs: t.executionTimeMs,
      }))
    : (assistantMessage?.chat_function_result?.map((fr) => ({
        name: fr.function_name,
        executionTimeMs: fr.execution_time ?? 0,
        functionCalls: fr.function_calls,
        resultData: fr.result_data,
      })) ?? []);
  const footerDateLabel = assistantMessage?.created_at
    ? new Date(assistantMessage.created_at).toLocaleString([], {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : new Date().toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });

  const currentReport: Report | null =
    activeReportId != null
      ? {
          id: activeReportId,
          title: activeConversation?.title ?? 'Report',
          type: 'AI-Generated',
          status: isGeneratingThisReport ? 'generating' : 'ready',
          createdAt: activeConversation?.updated_at
            ? new Date(activeConversation.updated_at)
            : new Date(),
          content: displayContent,
        }
      : null;

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b px-4 py-3">
        <BarChart3 className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">Reports</span>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto h-8 gap-1.5 text-muted-foreground"
          onClick={handleNewReport}
        >
          <Plus className="h-4 w-4" />
          <span className="text-xs md:text-sm">New report</span>
        </Button>
      </div>

      {currentReport != null ? (
        <ReportPreview
          report={currentReport}
          onClose={handleCloseReport}
          onDownload={() => handleDownload(currentReport)}
          isGenerating={isGeneratingThisReport}
          prompt={promptText}
          footer={{
            toolCount,
            toolDetails: footerToolDetails,
            tokens: assistantMessage?.tokens ?? null,
            responseTimeMs: assistantMessage?.response_time ?? null,
            dateLabel: footerDateLabel,
          }}
        />
      ) : (
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="shrink-0 border-b px-3">
            <TabsList className="h-9 w-full justify-start gap-1 bg-transparent p-0">
              <TabsTrigger
                value="create"
                className="h-8 rounded-none border-b-2 border-transparent px-3 data-[state=active]:border-primary data-[state=active]:bg-transparent"
              >
                Create
              </TabsTrigger>
              <TabsTrigger
                value="history"
                className="h-8 gap-1.5 rounded-none border-b-2 border-transparent px-3 data-[state=active]:border-primary data-[state=active]:bg-transparent"
              >
                History
                {reports.length > 0 && (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                    {reports.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="create" className="sr-only" />
          <TabsContent value="history" className="sr-only" />
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" role="tabpanel">
            {activeTab === 'create' && (
              <ReportBuilder
                onGenerate={handleGenerate}
                isGenerating={createReportMutation.isPending}
                initialPrompt={rerunPrompt}
                onInitialPromptConsumed={() => setRerunPrompt(null)}
              />
            )}
            {activeTab === 'history' && (
              <ReportHistory
                reports={reports}
                onPreview={handleOpenReport}
                onDownload={handleDownload}
                onRerun={handleRerun}
                onDelete={handleDelete}
                isDeleting={archiveMutation.isPending}
              />
            )}
          </div>
        </Tabs>
      )}
    </div>
  );
}
